import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box, Snackbar, Alert } from '@mui/material';
import {
    Dashboard, Business, CreditCard, Extension, LocationCity, AccountBalance, Category,
    People, Leaderboard, AccountBalanceWallet, Groups, Report, Diversity3, Work,
    Flag, Devices, Settings, Sms, Campaign, Fingerprint, Dns
} from '@mui/icons-material';

import { useAuth } from './contexts/AuthContext';
import { AuthProvider } from './contexts/AuthContext';
// Layouts
import SuperAdminLayout from './layouts/SuperAdminLayout';
import AdminLayout from './layouts/AdminLayout';

// Auth Pages
import Login from './pages/auth/Login';

// SuperAdmin Pages
import SuperAdminDashboard from './pages/superadmin/Dashboard';
import Plans from './pages/superadmin/Plans';
import Addons from './pages/superadmin/Addons';
import Tenants from './pages/superadmin/Tenants';
import TenantDetail from './pages/superadmin/TenantDetail';
import SubscriptionDetail from './pages/superadmin/SubscriptionDetail';
import Mandals from './pages/superadmin/Mandals';
import Districts from './pages/superadmin/Districts';
import ComplaintDepartments from './pages/superadmin/ComplaintDepartments';
import ComplaintTypes from './pages/superadmin/ComplaintTypes';
import MasterConstituencies from './pages/superadmin/MasterConstituencies';
import MasterConstituencyDetail from './pages/superadmin/MasterConstituencyDetail';
import MasterVillageDetail from './pages/superadmin/MasterVillageDetail';
import MasterVotingBoothDetail from './pages/superadmin/MasterVotingBoothDetail';
import Communities from './pages/superadmin/Communities';
import Professions from './pages/superadmin/Professions';
import PoliticalParties from './pages/superadmin/PoliticalParties';
import Ministries from './pages/superadmin/Ministries';
import ActiveSessions from './pages/superadmin/ActiveSessions';
import SiteSettings from './pages/superadmin/SiteSettings';
import SmsTemplates from './pages/superadmin/SmsTemplates';
import PdfAuditLogs from './pages/superadmin/PdfAuditLogs';
import Server from './pages/superadmin/Server';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminSubLeaders from './pages/admin/SubLeaders';
import Activities from './pages/admin/Activities';
import AdminWallet from './pages/admin/Wallet';
import CartCheckout from './pages/admin/CartCheckout'; // Needed for subscription flow
import AdminCitizens from './pages/admin/Citizens';
import ConstituencyDetails from './pages/admin/ConstituencyDetails';
import VillageDetails from './pages/admin/VillageDetails';
import VotingBoothDetails from './pages/admin/VotingBoothDetails';
import AllCitizens from './pages/admin/AllCitizens';
import Complaints from './pages/admin/Complaints';
import Campaigns from './pages/admin/Campaigns';

// Website Pages
import HomePage from './pages/website/HomePage';
import Profile from './pages/Profile';



// Placeholder pages (to be implemented)
const PlaceholderPage = ({ title }: { title: string }) => (
    <Box>
        <h2>{title}</h2>
        <p>This page is under construction.</p>
    </Box>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) => {
    const { isAuthenticated, isLoading, user } = useAuth();

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }

    if (allowedRoles && user && !allowedRoles.includes(user.role)) {
        // Redirect to appropriate dashboard based on role
        if (user.role === 'super_admin') return <Navigate to="/super-admin" />;
        if (user.role === 'admin') return <Navigate to="/admin" />;
        return <Navigate to="/dashboard" />;
    }

    return <>{children}</>;
};

// Subscription Required Wrapper - redirects to cart if no subscription
const SubscriptionRequired = ({ children }: { children: React.ReactNode }) => {
    const { hasActiveSubscription, user } = useAuth();

    // Super admin doesn't need subscription
    if (user?.role === 'super_admin') {
        return <>{children}</>;
    }

    if (!hasActiveSubscription()) {
        return <Navigate to="/admin/cart" />;
    }

    return <>{children}</>;
};

// Redirect to dashboard if subscription already exists
const UseSubscriptionExists = ({ children }: { children: React.ReactNode }) => {
    const { hasActiveSubscription } = useAuth();

    if (hasActiveSubscription()) {
        return <Navigate to="/admin" />;
    }

    return <>{children}</>;
};

// Redirect to dashboard if authenticated
const RedirectIfAuthenticated = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated, user, isLoading } = useAuth();

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isAuthenticated && user) {
        if (user.role === 'super_admin') return <Navigate to="/super-admin" />;
        if (user.role === 'admin' || user.role === 'sub_admin') return <Navigate to="/admin" />;
        return <Navigate to="/dashboard" />;
    }

    return <>{children}</>;
};

// Permission Required Wrapper - checks if sub_admin has required permission
const PermissionRequired = ({ children, permission }: { children: React.ReactNode; permission: string }) => {
    const { hasPermission, user } = useAuth();
    const [shouldRedirect, setShouldRedirect] = React.useState(false);
    const [showSnackbar, setShowSnackbar] = React.useState(false);

    React.useEffect(() => {
        if (user?.role === 'sub_admin' && !hasPermission(permission)) {
            setShowSnackbar(true);
            // Delay redirect to allow snackbar to show
            const timer = setTimeout(() => {
                setShouldRedirect(true);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [user, permission, hasPermission]);

    // Admin has full access to all pages
    if (user?.role === 'admin') {
        return <>{children}</>;
    }

    // Sub-admin must have the specific permission
    if (shouldRedirect) {
        return <Navigate to="/admin" replace />;
    }

    // Show snackbar if unauthorized
    if (showSnackbar) {
        return (
            <Snackbar open={showSnackbar} autoHideDuration={3000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" sx={{ width: '100%' }}>
                    You are not authorised to access this page.
                </Alert>
            </Snackbar>
        );
    }

    return <>{children}</>;
};

// Super Admin Menu Items
const superAdminMenuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/super-admin' },
    { text: 'Tenants', icon: <Business />, path: '/super-admin/tenants' },
    { text: 'Plans', icon: <CreditCard />, path: '/super-admin/plans' },
    { text: 'Addons', icon: <Extension />, path: '/super-admin/addons' },
    { text: 'Districts', icon: <LocationCity />, path: '/super-admin/districts' },
    { text: 'Mandals', icon: <LocationCity />, path: '/super-admin/mandals' },
    { text: 'Master Data', icon: <AccountBalance />, path: '/super-admin/master-constituencies' },
    { text: 'Request Depts', icon: <AccountBalance />, path: '/super-admin/complaint-departments' },
    { text: 'Request Types', icon: <Category />, path: '/super-admin/complaint-types' },
    { text: 'Communities', icon: <Diversity3 />, path: '/super-admin/communities' },
    { text: 'Professions', icon: <Work />, path: '/super-admin/professions' },
    { text: 'Pro Parties', icon: <Flag />, path: '/super-admin/political-parties' },
    { text: 'Ministries', icon: <AccountBalance />, path: '/super-admin/ministries' },
    { text: 'Active Sessions', icon: <Devices />, path: '/super-admin/active-sessions' },
    { text: 'Site Settings', icon: <Settings />, path: '/super-admin/site-settings' },
    { text: 'SMS Templates', icon: <Sms />, path: '/super-admin/sms-templates' },
    { text: 'PDF Audit Logs', icon: <Fingerprint />, path: '/super-admin/pdf-audit-logs' },
    { text: 'Server', icon: <Dns />, path: '/super-admin/server' },
];

// Admin Menu Items
const adminMenuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin', permission: 'dashboard.view' },
    { text: 'Users', icon: <People />, path: '/admin/users', permission: 'users.view' },
    { text: 'Constituencies', icon: <Leaderboard />, path: '/admin/citizens', permission: 'sub-leaders.view' },
    { text: 'All Citizens', icon: <Groups />, path: '/admin/all-citizens', permission: 'citizens.view' },
    { text: 'Leaders', icon: <Groups />, path: '/admin/sub-leaders', permission: 'sub_leaders.view' },
    { text: 'Citizen Requests', icon: <Report />, path: '/admin/complaints', permission: 'complaints.view' },
    { text: 'Activities', icon: <Work />, path: '/admin/activities', permission: 'development_activities.view' },
    // { text: 'Wallet', icon: <AccountBalanceWallet />, path: '/admin/wallet', permission: 'wallet.view' },
    // { text: 'Campaigns', icon: <Campaign />, path: '/admin/campaigns', permission: 'campaigns.view' },
];

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={
                <RedirectIfAuthenticated>
                    <Login />
                </RedirectIfAuthenticated>
            } />

            {/* Super Admin Routes */}
            <Route
                path="/super-admin"
                element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                        <SuperAdminLayout menuItems={superAdminMenuItems} />
                    </ProtectedRoute>
                }
            >
                <Route index element={<SuperAdminDashboard />} />
                <Route path="tenants" element={<Tenants />} />
                <Route path="tenants/:tenantId" element={<TenantDetail />} />
                <Route path="tenants/:tenantId/subscriptions/:subscriptionId" element={<SubscriptionDetail />} />
                <Route path="plans" element={<Plans />} />
                <Route path="addons" element={<Addons />} />
                <Route path="districts" element={<Districts />} />
                <Route path="mandals" element={<Mandals />} />
                <Route path="master-constituencies" element={<MasterConstituencies />} />
                <Route path="master-constituencies/:masterConstituencyId" element={<MasterConstituencyDetail />} />
                <Route path="master-constituencies/:masterConstituencyId/villages/:masterVillageId" element={<MasterVillageDetail />} />
                <Route path="master-constituencies/:masterConstituencyId/villages/:masterVillageId/voting-booths/:masterVotingBoothId" element={<MasterVotingBoothDetail />} />
                <Route path="complaint-departments" element={<ComplaintDepartments />} />
                <Route path="complaint-types" element={<ComplaintTypes />} />
                <Route path="communities" element={<Communities />} />
                <Route path="professions" element={<Professions />} />
                <Route path="political-parties" element={<PoliticalParties />} />
                <Route path="ministries" element={<Ministries />} />
                <Route path="active-sessions" element={<ActiveSessions />} />
                <Route path="site-settings" element={<SiteSettings />} />
                <Route path="sms-templates" element={<SmsTemplates />} />
                <Route path="pdf-audit-logs" element={<PdfAuditLogs />} />
                <Route path="server" element={<Server />} />
                <Route path="profile" element={<Profile />} />
            </Route>

            {/* Admin Routes */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'sub_admin']}>
                        <AdminLayout menuItems={adminMenuItems} />
                    </ProtectedRoute>
                }
            >
                {/* Cart page - accessible without subscription (but redirect if already has one) */}
                <Route path="cart" element={
                    <UseSubscriptionExists>
                        <CartCheckout />
                    </UseSubscriptionExists>
                } />

                {/* All other routes require subscription and permission */}
                <Route index element={<SubscriptionRequired><AdminDashboard /></SubscriptionRequired>} />
                <Route path="users" element={<SubscriptionRequired><PermissionRequired permission="users.view"><AdminUsers /></PermissionRequired></SubscriptionRequired>} />
                <Route path="sub-leaders" element={<SubscriptionRequired><PermissionRequired permission="sub-leaders.view"><AdminSubLeaders /></PermissionRequired></SubscriptionRequired>} />
                <Route path="all-citizens" element={<SubscriptionRequired><PermissionRequired permission="citizens.view"><AllCitizens /></PermissionRequired></SubscriptionRequired>} />
                <Route path="citizens" element={<SubscriptionRequired><PermissionRequired permission="citizens.view"><AdminCitizens /></PermissionRequired></SubscriptionRequired>} />
                <Route path="citizens/:constituencyId" element={<SubscriptionRequired><PermissionRequired permission="citizens.view"><ConstituencyDetails /></PermissionRequired></SubscriptionRequired>} />
                <Route path="citizens/:constituencyId/villages/:villageId" element={<SubscriptionRequired><PermissionRequired permission="citizens.view"><VillageDetails /></PermissionRequired></SubscriptionRequired>} />
                <Route path="citizens/:constituencyId/villages/:villageId/booths/:boothId" element={<SubscriptionRequired><PermissionRequired permission="citizens.view"><VotingBoothDetails /></PermissionRequired></SubscriptionRequired>} />
                <Route path="complaints" element={<SubscriptionRequired><PermissionRequired permission="complaints.view"><Complaints /></PermissionRequired></SubscriptionRequired>} />
                <Route path="activities" element={<SubscriptionRequired><PermissionRequired permission="development_activities.view"><Activities /></PermissionRequired></SubscriptionRequired>} />
                <Route path="wallet" element={<SubscriptionRequired><PermissionRequired permission="wallet.view"><AdminWallet /></PermissionRequired></SubscriptionRequired>} />
                <Route path="campaigns" element={<SubscriptionRequired><PermissionRequired permission="campaigns.view"><Campaigns /></PermissionRequired></SubscriptionRequired>} />
                <Route path="profile" element={<Profile />} />
            </Route>

            {/* Sub Admin Routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute allowedRoles={['sub_admin']}>
                        <AdminLayout menuItems={[{ text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' }]} />
                    </ProtectedRoute>
                }
            >
                <Route index element={<PlaceholderPage title="Sub Admin Dashboard" />} />
                <Route path="profile" element={<Profile />} />
            </Route>

            {/* Catch-all redirect to homepage */}
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppRoutes />
        </AuthProvider>
    );
}

export default App;
